/**
 * /guides — Local Guide console.
 *
 *   /guides                       — my claims (pending + approved)
 *   /guides/:citySlug*            — one city's POI moderation dashboard
 *
 * MVP scope:
 *   • Claim / see status of a city guide badge.
 *   • Once approved, review every POI in the city grouped by
 *     category and one-click Verify or Hide.
 *   • Photo moderation already lives in /admin (linked from this
 *     page). This screen focuses on POI hygiene.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Loader2, EyeOff, CheckCircle2, MapPin, ArrowLeft, Sparkles,
  Upload, Trash2, ImagePlus, Edit3, X, Save, AlertTriangle, RefreshCw,
  ImageIcon, XCircle, Camera,
} from "lucide-react";
import PlaceStamp, { primeStampCache, evictStampCache } from "@/components/PlaceStamp";

/**
 * PHOTO_REJECT_REASONS — one-tap chip presets for the photo
 * moderation modal. Keeping the roster tight (six labels) so guides
 * pick fast without scanning a long menu; `text` is what actually
 * lands in the submitter's rejection email.
 */
const PHOTO_REJECT_REASONS = [
  { key: "blurry",       label: "Blurry",         text: "The photo is too blurry to read clearly." },
  { key: "off-topic",    label: "Off-topic",      text: "This photo isn't of the location it was submitted for." },
  { key: "duplicate",    label: "Duplicate",      text: "This photo is already published on the site." },
  { key: "wrong-place",  label: "Wrong location", text: "The photo shows a different place than the one listed." },
  { key: "inappropriate",label: "Inappropriate",  text: "The photo includes content we can't publish on Social Pulse." },
  { key: "low-quality",  label: "Low quality",    text: "The photo's resolution or framing isn't up to publication quality." },
];

export default function GuidesConsole() {
  const loc = useLocation();
  const nav = useNavigate();

  // Path parsing: /guides/<...city-slug>
  const citySlug = useMemo(() => {
    const raw = loc.pathname.replace(/^\/guides\/?/, "").replace(/\/+$/, "");
    return raw.toLowerCase();
  }, [loc.pathname]);

  const [claims, setClaims] = useState([]);
  const [loadingClaims, setLoadingClaims] = useState(true);

  useEffect(() => {
    let alive = true;
    http.get("/geo/places/guides/me/claims")
      .then((r) => alive && setClaims(r.data?.claims || []))
      .catch(() => alive && setClaims([]))
      .finally(() => alive && setLoadingClaims(false));
    return () => { alive = false; };
  }, []);

  if (!citySlug) {
    return <GuideHome claims={claims} loading={loadingClaims} nav={nav} />;
  }
  return <CityGuideConsole citySlug={citySlug} claims={claims} />;
}

/** Landing page — lists every city the current user has claimed. */
function GuideHome({ claims, loading, nav }) {
  const [newSlug, setNewSlug] = useState("");
  const claim = async () => {
    if (!newSlug.trim()) return;
    try {
      const r = await http.post(`/geo/places/guides/claim/${newSlug.trim()}`);
      toast.success(`Claim submitted (${r.data.status})`);
      nav(`/guides/${newSlug.trim()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-6 py-10" data-testid="guides-home">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-6 h-6" style={{ color: "#0E7490" }} />
        <h1 className="font-display text-3xl">Local Guide Console</h1>
      </div>
      <p className="text-sm opacity-70 mb-8">
        Trusted locals help us keep every city directory accurate. Claim a city
        below, then verify or hide POIs once your claim is approved by an admin.
      </p>

      {/* Claim a new city */}
      <div className="wofy-card mb-8">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
          <Sparkles className="w-3 h-3 inline -mt-0.5 mr-1" /> CLAIM A CITY
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            value={newSlug}
            onChange={(e) => setNewSlug(e.target.value)}
            placeholder="e.g. united-states/oregon/portland"
            className="flex-1 min-w-[240px] rounded-lg border px-3 py-2 text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="guide-claim-input"
          />
          <button
            onClick={claim}
            className="wofy-btn-primary"
            data-testid="guide-claim-btn"
          >
            Request badge
          </button>
        </div>
        <p className="text-xs opacity-60 mt-2">
          Enter a location slug (as it appears in the URL after <code>/l/</code>).
        </p>
      </div>

      {/* My claims */}
      <div className="wofy-card">
        <div className="overline mb-3" style={{ color: "var(--wofy-primary)" }}>
          <ShieldCheck className="w-3 h-3 inline -mt-0.5 mr-1" /> MY CLAIMS
        </div>
        {loading ? (
          <div className="text-sm opacity-60"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
        ) : claims.length === 0 ? (
          <p className="text-sm opacity-70">No claims yet. Claim a city above to get started.</p>
        ) : (
          <ul className="divide-y" style={{ borderColor: "var(--wofy-border)" }} data-testid="guide-claims-list">
            {claims.map((c) => (
              <li key={c.city_slug} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{c.city_name || c.city_slug}</div>
                  <div className="text-xs opacity-70 truncate">{c.city_slug}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <StatusBadge status={c.status} />
                  <Link
                    to={`/guides/${c.city_slug}`}
                    className="text-sm underline"
                    data-testid={`guide-open-${c.city_slug}`}
                  >
                    Open →
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Per-city dashboard — lists POIs to verify or hide. */
function CityGuideConsole({ citySlug, claims }) {
  const [directory, setDirectory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);   // slug that's currently being verified/hidden

  const claim = useMemo(
    () => (claims || []).find((c) => c.city_slug === citySlug) || null,
    [claims, citySlug]
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    http.get(`/geo/places/directory/${citySlug}`)
      .then((r) => alive && setDirectory(r.data))
      .catch(() => alive && setDirectory(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [citySlug]);

  const act = async (poiSlug, action) => {
    setBusy(poiSlug);
    try {
      await http.post(`/geo/places/${poiSlug}/${action}`);
      toast.success(action === "verify" ? "POI verified" : "POI hidden");
      // Optimistically remove hidden POIs; mark verified POIs.
      setDirectory((prev) => {
        if (!prev) return prev;
        const cats = prev.categories.map((cat) => ({
          ...cat,
          items: cat.items
            .filter((p) => !(p.slug === poiSlug && action === "hide"))
            .map((p) => (p.slug === poiSlug && action === "verify" ? { ...p, verified: true } : p)),
        })).filter((cat) => cat.items.length > 0);
        return { ...prev, categories: cats };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't ${action}`);
    } finally {
      setBusy(null);
    }
  };

  /** Optimistically merge inline-edit patch into the directory tree. */
  const applyEdit = (poiSlug, patch) => {
    setDirectory((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        categories: prev.categories.map((cat) => ({
          ...cat,
          items: cat.items.map((p) => (p.slug === poiSlug ? { ...p, ...patch } : p)),
        })),
      };
    });
  };

  const status = claim?.status || "none";
  const canAct = status === "approved" || status === "admin";

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-6 py-10" data-testid={`guides-city-${citySlug}`}>
      <Link to="/guides" className="text-xs font-mono uppercase tracking-widest opacity-70 hover:underline inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> All claims
      </Link>

      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-6 h-6" style={{ color: "#0E7490" }} />
        <h1 className="font-display text-3xl">{directory?.city_name || citySlug}</h1>
      </div>
      <div className="flex items-center gap-2 mb-6">
        <StatusBadge status={status} />
        <Link to={`/l/${citySlug}`} className="text-xs underline opacity-70 hover:opacity-100">
          View public page →
        </Link>
      </div>

      {status === "none" && (
        <div className="wofy-card mb-6">
          <p className="text-sm">
            You haven't claimed this city yet.{" "}
            <button
              onClick={async () => {
                try {
                  await http.post(`/geo/places/guides/claim/${citySlug}`);
                  toast.success("Claim submitted");
                  window.location.reload();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Claim failed");
                }
              }}
              className="underline font-medium"
              data-testid="guide-inline-claim"
            >
              Claim it now
            </button>
            .
          </p>
        </div>
      )}

      {status === "pending" && (
        <div className="wofy-card mb-6" style={{ background: "#FFFBEB", borderColor: "#FCD34D" }}>
          <p className="text-sm">
            Your claim is <strong>pending admin review</strong>. You can browse
            the directory below, but verify / hide actions unlock once approved.
          </p>
        </div>
      )}

      {canAct && <CityCrestPanel citySlug={citySlug} cityName={directory?.city_name} />}
      {canAct && <PhotoModerationPanel citySlug={citySlug} />}
      {canAct && <ClosureSweepPanel citySlug={citySlug} />}

      {loading ? (
        <div className="text-sm opacity-60"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading directory…</div>
      ) : !directory || !directory.categories?.length ? (
        <div className="wofy-card">
          <p className="text-sm opacity-70">
            No POIs indexed for this city yet — our Geographic Discovery Engine
            will populate this shortly.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {directory.categories.map((cat) => (
            <section key={cat.category} data-testid={`guide-cat-${cat.category}`}>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 opacity-70" />
                <h2 className="font-display text-xl">{cat.label}</h2>
                <span className="text-xs font-mono opacity-60">{cat.count}</span>
              </div>
              <ul className="divide-y rounded-xl border" style={{ borderColor: "var(--wofy-border)" }}>
                {cat.items.map((p) => (
                  <POIRow
                    key={p.slug}
                    p={p}
                    canAct={canAct}
                    busy={busy === p.slug}
                    onVerify={() => act(p.slug, "verify")}
                    onHide={() => act(p.slug, "hide")}
                    onEdited={(patch) => applyEdit(p.slug, patch)}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

/** Single POI row — read-only summary with inline-edit affordance. */
function POIRow({ p, canAct, busy, onVerify, onHide, onEdited }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name:    p.name || "",
    address: p.address || "",
    phone:   p.phone || "",
    website: p.website || "",
    email:   p.email || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    // Only send fields that actually changed to keep the audit log clean.
    const changed = {};
    for (const k of ["name", "address", "phone", "website", "email"]) {
      const before = p[k] || "";
      const after  = form[k] || "";
      if (before.trim() !== after.trim()) changed[k] = after.trim();
    }
    if (!Object.keys(changed).length) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const r = await http.patch(`/geo/places/${p.slug}/edit`, changed);
      onEdited(r.data.poi || changed);
      toast.success("POI updated");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <li className="flex items-center gap-3 px-3 py-2" data-testid={`guide-poi-${p.slug}`}>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">
            {p.name}
            {p.verified && (
              <CheckCircle2 className="w-3.5 h-3.5 inline ml-1.5" style={{ color: "#15803D" }} />
            )}
            {p.closure_suspected && (
              <span
                className="ml-2 text-[10px] font-mono uppercase tracking-widest rounded-full px-1.5 py-0.5"
                style={{ background: "#FEF3C7", color: "#92400E" }}
                data-testid={`guide-poi-closure-flag-${p.slug}`}
                title={p.closure_reason || "Suspected closure"}
              >
                Suspected closure
              </span>
            )}
          </div>
          {p.address && <div className="text-xs opacity-70 truncate">{p.address}</div>}
          {(p.phone || p.website) && (
            <div className="text-[11px] opacity-60 truncate">
              {p.phone}{p.phone && p.website ? " · " : ""}{p.website}
            </div>
          )}
        </div>
        {canAct && (
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => setEditing(true)}
              disabled={busy}
              className="text-xs px-2 py-1 rounded-md border inline-flex items-center gap-1 hover:brightness-95 disabled:opacity-50"
              style={{ borderColor: "#BFDBFE", color: "#1E40AF", background: "#EFF6FF" }}
              title="Edit name / address / phone / website / email"
              data-testid={`guide-edit-${p.slug}`}
            >
              <Edit3 className="w-3 h-3" /> Edit
            </button>
            <button
              onClick={onVerify}
              disabled={busy || p.verified}
              className="text-xs px-2 py-1 rounded-md border inline-flex items-center gap-1 hover:brightness-95 disabled:opacity-50"
              style={{ borderColor: "#86EFAC", color: "#166534", background: "#F0FDF4" }}
              title="Mark this POI as human-verified"
              data-testid={`guide-verify-${p.slug}`}
            >
              <CheckCircle2 className="w-3 h-3" /> Verify
            </button>
            <button
              onClick={onHide}
              disabled={busy}
              className="text-xs px-2 py-1 rounded-md border inline-flex items-center gap-1 hover:brightness-95 disabled:opacity-50"
              style={{ borderColor: "#FCA5A5", color: "#B91C1C", background: "#FEF2F2" }}
              title="Hide this POI (soft delete)"
              data-testid={`guide-hide-${p.slug}`}
            >
              <EyeOff className="w-3 h-3" /> Hide
            </button>
          </div>
        )}
      </li>
    );
  }

  return (
    <li className="px-3 py-3" data-testid={`guide-poi-edit-${p.slug}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <label className="text-xs">
          <span className="opacity-60 block mb-0.5">Name</span>
          <input
            type="text" value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full rounded-md border px-2 py-1 text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid={`guide-edit-name-${p.slug}`}
          />
        </label>
        <label className="text-xs">
          <span className="opacity-60 block mb-0.5">Phone</span>
          <input
            type="text" value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            className="w-full rounded-md border px-2 py-1 text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid={`guide-edit-phone-${p.slug}`}
          />
        </label>
        <label className="text-xs sm:col-span-2">
          <span className="opacity-60 block mb-0.5">Address</span>
          <input
            type="text" value={form.address}
            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            className="w-full rounded-md border px-2 py-1 text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid={`guide-edit-address-${p.slug}`}
          />
        </label>
        <label className="text-xs">
          <span className="opacity-60 block mb-0.5">Website</span>
          <input
            type="url" value={form.website}
            onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
            className="w-full rounded-md border px-2 py-1 text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid={`guide-edit-website-${p.slug}`}
          />
        </label>
        <label className="text-xs">
          <span className="opacity-60 block mb-0.5">Email</span>
          <input
            type="email" value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="w-full rounded-md border px-2 py-1 text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid={`guide-edit-email-${p.slug}`}
          />
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={() => setEditing(false)}
          disabled={saving}
          className="text-xs px-2 py-1 rounded-md border inline-flex items-center gap-1"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid={`guide-edit-cancel-${p.slug}`}
        >
          <X className="w-3 h-3" /> Cancel
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="text-xs px-2 py-1 rounded-md text-white inline-flex items-center gap-1 disabled:opacity-60"
          style={{ background: "var(--wofy-primary)" }}
          data-testid={`guide-edit-save-${p.slug}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
        </button>
      </div>
    </li>
  );
}

/** Closure-sweep panel — re-scan OSM + list currently-flagged POIs. */
function ClosureSweepPanel({ citySlug }) {
  const [sweeping, setSweeping] = useState(false);
  const [closures, setClosures] = useState([]);
  const [lastRun, setLastRun] = useState(null);

  const refresh = async () => {
    try {
      const r = await http.get(`/geo/places/closures/${citySlug}`);
      setClosures(r.data?.items || []);
    } catch { setClosures([]); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [citySlug]);

  const sweep = async () => {
    setSweeping(true);
    try {
      const r = await http.post(`/geo/places/closure-sweep/${citySlug}`);
      setLastRun(r.data);
      toast.success(`Sweep done — ${r.data.total_flagged} flagged, ${r.data.total_cleared} cleared`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed");
    } finally {
      setSweeping(false);
    }
  };

  return (
    <section className="wofy-card mb-6" data-testid="guide-closure-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
            <AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-1" /> CLOSURE SWEEP
          </div>
          <h3 className="font-display text-lg leading-tight">
            {closures.length
              ? `${closures.length} POI${closures.length === 1 ? "" : "s"} flagged as possibly closed`
              : "Everything looks open"}
          </h3>
          <p className="text-xs opacity-70 mt-1 max-w-[52ch]">
            Re-scans OpenStreetMap and flags POIs whose source record disappeared —
            usually a strong signal the business shut down. Nothing is auto-hidden;
            you review each flag manually.
          </p>
        </div>
        <button
          onClick={sweep}
          disabled={sweeping}
          className="wofy-btn-primary inline-flex items-center gap-1.5 disabled:opacity-60 shrink-0"
          data-testid="guide-closure-sweep-btn"
        >
          {sweeping ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {sweeping ? "Sweeping…" : "Run sweep now"}
        </button>
      </div>

      {closures.length > 0 && (
        <ul
          className="mt-4 divide-y rounded-lg border"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="guide-closure-list"
        >
          {closures.map((c) => (
            <li
              key={c.slug}
              className="flex items-center gap-2 px-3 py-2 text-sm"
              data-testid={`guide-closure-item-${c.slug}`}
            >
              <span className="flex-1 min-w-0 truncate">
                <span className="font-medium">{c.name}</span>
                {c.address && <span className="opacity-60"> · {c.address}</span>}
              </span>
              <span className="text-[10px] font-mono uppercase tracking-widest opacity-60 shrink-0">
                {c.category}
              </span>
            </li>
          ))}
        </ul>
      )}

      {lastRun?.results?.length > 0 && (
        <p className="text-[11px] font-mono opacity-60 mt-3" data-testid="guide-closure-last-run">
          Last sweep: {lastRun.total_flagged} newly flagged, {lastRun.total_cleared} cleared across {lastRun.results.length} categories.
        </p>
      )}
    </section>
  );
}

function StatusBadge({ status }) {
  const map = {
    approved: { label: "Approved",  bg: "#DCFCE7", color: "#166534" },
    pending:  { label: "Pending",   bg: "#FEF3C7", color: "#92400E" },
    rejected: { label: "Rejected",  bg: "#FEE2E2", color: "#B91C1C" },
    admin:    { label: "Admin",     bg: "#E0E7FF", color: "#3730A3" },
    none:     { label: "Unclaimed", bg: "#F1F5F9", color: "#334155" },
  };
  const s = map[status] || map.none;
  return (
    <span
      className="text-[10px] font-mono uppercase tracking-widest rounded-full px-2 py-0.5"
      style={{ background: s.bg, color: s.color }}
      data-testid={`guide-status-${status}`}
    >
      {s.label}
    </span>
  );
}

/**
 * CityCrestPanel — upload / preview / clear the city's real crest.
 *
 * Backend contract:
 *   GET    /geo/places/stamp/{citySlug}   → { stamp_url, updated_by, updated_at }
 *   POST   /geo/places/stamp/{citySlug}   → upload (base64 + mime); guide/admin only
 *   DELETE /geo/places/stamp/{citySlug}   → clear override
 *
 * Client-side guard: 512 KB decoded, PNG/JPEG/WEBP/SVG only — mirrors
 * the backend limits so users get instant feedback.
 */
const CREST_MAX_BYTES = 512 * 1024;
const CREST_ALLOWED_MIME = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];

function CityCrestPanel({ citySlug, cityName }) {
  const inputRef = useRef(null);
  const [current, setCurrent] = useState(null);   // { stamp_url, updated_by, updated_at }
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await http.get(`/geo/places/stamp/${citySlug}`);
      setCurrent(r.data || null);
      primeStampCache(citySlug, r.data?.stamp_url || null);
    } catch {
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [citySlug]);

  const onFile = async (file) => {
    if (!file) return;
    if (!CREST_ALLOWED_MIME.includes(file.type)) {
      toast.error("Use PNG, JPEG, WEBP or SVG.");
      return;
    }
    if (file.size > CREST_MAX_BYTES) {
      toast.error(`Crest is too large — max ${Math.round(CREST_MAX_BYTES / 1024)} KB.`);
      return;
    }
    setBusy(true);
    try {
      // FileReader → base64 (strip the data:...; prefix — backend adds
      // it back once it recomposes the data URI).
      const b64 = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => {
          const s = String(fr.result || "");
          const idx = s.indexOf(",");
          resolve(idx >= 0 ? s.slice(idx + 1) : s);
        };
        fr.onerror = () => reject(fr.error);
        fr.readAsDataURL(file);
      });
      const r = await http.post(`/geo/places/stamp/${citySlug}`, {
        mime_type:   file.type === "image/jpg" ? "image/jpeg" : file.type,
        data_base64: b64,
      });
      setCurrent({
        stamp_url:  r.data.stamp_url,
        updated_by: r.data.updated_by,
        updated_at: r.data.updated_at,
      });
      primeStampCache(citySlug, r.data.stamp_url);
      toast.success("City crest updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = async () => {
    if (!current?.stamp_url) return;
    if (!window.confirm("Remove the uploaded crest? The auto-picked stamp will show again.")) return;
    setBusy(true);
    try {
      await http.delete(`/geo/places/stamp/${citySlug}`);
      setCurrent(null);
      evictStampCache(citySlug);
      toast.success("Crest cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="wofy-card mb-6"
      style={{ borderColor: "var(--wofy-border)" }}
      data-testid="guide-crest-panel"
    >
      <div className="flex items-start gap-4">
        {/* Preview — real uploaded crest wins, otherwise auto-pick */}
        <div className="shrink-0" data-testid="guide-crest-preview">
          {loading ? (
            <div className="w-[88px] h-[88px] rounded-full bg-slate-100 animate-pulse" />
          ) : (
            <PlaceStamp
              kind="city"
              slug={citySlug}
              override={current?.stamp_url || null}
              size={88}
              className="shadow-sm ring-2 ring-white/70"
              title={cityName || citySlug}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
            <ImagePlus className="w-3 h-3 inline -mt-0.5 mr-1" /> CITY CREST
          </div>
          <h3 className="font-display text-lg leading-tight">
            {current?.stamp_url ? "Your custom crest is live" : "Upload the real crest or seal"}
          </h3>
          <p className="text-xs opacity-70 mt-1 max-w-[52ch]">
            Replace the auto-picked illustration with your city's official crest,
            seal or emblem. Approved guides can upload PNG, JPEG, WEBP or SVG
            (max {Math.round(CREST_MAX_BYTES / 1024)} KB). It'll appear everywhere
            this city is listed — hero, directories and sibling sites.
          </p>
          {current?.updated_by && (
            <p className="text-[11px] font-mono opacity-60 mt-1" data-testid="guide-crest-meta">
              Last updated by {current.updated_by}
              {current.updated_at ? ` · ${new Date(current.updated_at).toLocaleDateString()}` : ""}
            </p>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <input
              ref={inputRef}
              type="file"
              accept={CREST_ALLOWED_MIME.join(",")}
              onChange={(e) => onFile(e.target.files?.[0])}
              className="hidden"
              data-testid="guide-crest-file"
            />
            <button
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="wofy-btn-primary inline-flex items-center gap-1.5 disabled:opacity-60"
              data-testid="guide-crest-upload-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {current?.stamp_url ? "Replace crest" : "Upload crest"}
            </button>
            {current?.stamp_url && (
              <button
                onClick={clear}
                disabled={busy}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border disabled:opacity-60"
                style={{ borderColor: "#FCA5A5", color: "#B91C1C", background: "#FEF2F2" }}
                data-testid="guide-crest-clear-btn"
              >
                <Trash2 className="w-4 h-4" /> Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/** Per-city photo moderation lane — approve / reject neighbour-submitted
 * photos without leaving the guide console.
 *
 * Backend contract:
 *   GET  /photos/pending/city/{citySlug}?status=pending    → { items }
 *   POST /photos/guide/{submissionId}/approve  { slot?, note? }
 *   POST /photos/guide/{submissionId}/reject   { reason? }
 */
function PhotoModerationPanel({ citySlug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);        // submission id in flight
  const [rejectFor, setRejectFor] = useState(null); // { id, reason }
  const [tab, setTab] = useState("pending");     // pending | approved | rejected

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await http.get(`/photos/pending/city/${citySlug}?status=${tab}`);
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [citySlug, tab]);

  const approve = async (id) => {
    setBusy(id);
    try {
      await http.post(`/photos/guide/${id}/approve`, {});
      toast.success("Photo approved");
      setItems((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setBusy(null); }
  };

  const submitReject = async (presetReason) => {
    if (!rejectFor?.id) return;
    // Preset chip wins when clicked; fall back to whatever the guide
    // typed in the custom textarea. Both are optional — an empty
    // reason still rejects the photo, the submitter just gets a
    // generic apology email.
    const finalReason = (presetReason ?? rejectFor.reason ?? "").trim();
    setBusy(rejectFor.id);
    try {
      await http.post(`/photos/guide/${rejectFor.id}/reject`, {
        reason: finalReason || null,
      });
      toast.success(finalReason ? `Rejected — “${finalReason}”` : "Photo rejected");
      setItems((prev) => prev.filter((p) => p.id !== rejectFor.id));
      setRejectFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally { setBusy(null); }
  };

  const pendingCount = tab === "pending" ? items.length : null;

  return (
    <section className="wofy-card mb-6" data-testid="guide-photo-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
            <Camera className="w-3 h-3 inline -mt-0.5 mr-1" /> PHOTO MODERATION
          </div>
          <h3 className="font-display text-lg leading-tight">
            {tab === "pending" && pendingCount === 0
              ? "Inbox zero — no photos waiting"
              : `Neighbour photos for this city`}
          </h3>
          <p className="text-xs opacity-70 mt-1 max-w-[52ch]">
            Photos submitted for locations inside your claimed city land
            here. Approve to publish; reject to remove. The submitter is
            emailed either way.
          </p>
        </div>
        <div className="inline-flex rounded-full border overflow-hidden shrink-0" style={{ borderColor: "var(--wofy-border)" }} data-testid="guide-photo-tabs">
          {["pending", "approved", "rejected"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`text-xs font-mono uppercase tracking-widest px-3 py-1 ${tab === t ? "text-white" : ""}`}
              style={{ background: tab === t ? "var(--wofy-primary)" : "transparent" }}
              data-testid={`guide-photo-tab-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm opacity-70"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading photos…</div>
      ) : items.length === 0 ? (
        <div className="text-sm opacity-70 flex items-center gap-2" data-testid="guide-photo-empty">
          <ImageIcon className="w-4 h-4 opacity-60" />
          {tab === "pending"
            ? "No pending photos — encourage neighbours to submit shots from the location page."
            : `No ${tab} photos yet.`}
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="guide-photo-list">
          {items.map((p) => (
            <li
              key={p.id}
              className="rounded-xl border p-2 flex gap-3"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid={`guide-photo-${p.id}`}
            >
              {p.data_uri ? (
                <img
                  src={p.data_uri}
                  alt={p.title}
                  loading="lazy"
                  className="w-24 h-24 rounded-lg object-cover shrink-0"
                />
              ) : (
                <div className="w-24 h-24 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                  <ImageIcon className="w-6 h-6 opacity-40" />
                </div>
              )}
              <div className="min-w-0 flex-1 flex flex-col">
                <div className="text-sm font-medium truncate">{p.title}</div>
                <div className="text-[11px] opacity-60 truncate">
                  {p.submitter_name || "Anonymous"}
                  {p.target_slug ? ` · for /l/${p.target_slug}` : ""}
                </div>
                {p.description && (
                  <div className="text-xs opacity-80 mt-1 line-clamp-2">{p.description}</div>
                )}
                {tab === "pending" && (
                  <div className="mt-auto pt-2 flex gap-1.5">
                    <button
                      onClick={() => approve(p.id)}
                      disabled={busy === p.id}
                      className="text-xs px-2 py-1 rounded-md border inline-flex items-center gap-1 disabled:opacity-50"
                      style={{ borderColor: "#86EFAC", color: "#166534", background: "#F0FDF4" }}
                      data-testid={`guide-photo-approve-${p.id}`}
                    >
                      <CheckCircle2 className="w-3 h-3" /> Approve
                    </button>
                    <button
                      onClick={() => setRejectFor({ id: p.id, reason: "" })}
                      disabled={busy === p.id}
                      className="text-xs px-2 py-1 rounded-md border inline-flex items-center gap-1 disabled:opacity-50"
                      style={{ borderColor: "#FCA5A5", color: "#B91C1C", background: "#FEF2F2" }}
                      data-testid={`guide-photo-reject-${p.id}`}
                    >
                      <XCircle className="w-3 h-3" /> Reject
                    </button>
                  </div>
                )}
                {tab !== "pending" && (
                  <div className="mt-auto pt-2 text-[11px] font-mono uppercase tracking-widest opacity-60">
                    {tab}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Reject-with-reason mini modal */}
      {rejectFor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setRejectFor(null)}
          data-testid="guide-photo-reject-modal"
        >
          <div
            className="bg-white rounded-xl max-w-md w-full p-5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="font-display text-lg mb-2">Reject this photo?</h4>
            <p className="text-xs opacity-70 mb-3">
              Tap a common reason to reject in one tap, or type a custom
              note. Either way the submitter is emailed.
            </p>

            {/* One-tap reasons */}
            <div className="mb-3">
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1.5">
                One-tap reasons
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="guide-photo-reason-chips">
                {PHOTO_REJECT_REASONS.map((r) => (
                  <button
                    key={r.key}
                    onClick={() => submitReject(r.text)}
                    disabled={busy === rejectFor.id}
                    className="text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1 hover:bg-red-50 hover:brightness-95 disabled:opacity-50 transition"
                    style={{ borderColor: "#FCA5A5", color: "#B91C1C", background: "#FEF2F2" }}
                    data-testid={`guide-photo-reason-${r.key}`}
                    title={r.text}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-1">
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1.5">
                Or write a custom note
              </div>
              <textarea
                value={rejectFor.reason}
                onChange={(e) => setRejectFor((f) => ({ ...f, reason: e.target.value }))}
                placeholder="Optional reason for the submitter…"
                rows={3}
                className="w-full rounded-md border px-2 py-1.5 text-sm bg-transparent"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="guide-photo-reject-reason"
              />
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button
                onClick={() => setRejectFor(null)}
                className="text-xs px-2 py-1 rounded-md border"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="guide-photo-reject-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => submitReject()}
                disabled={busy === rejectFor.id}
                className="text-xs px-3 py-1 rounded-md text-white inline-flex items-center gap-1 disabled:opacity-60"
                style={{ background: "#B91C1C" }}
                data-testid="guide-photo-reject-confirm"
              >
                {busy === rejectFor.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                Reject with note
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

